import {
  Component,
  computed,
  signal,
  ViewChild,
  input,
  effect,
  linkedSignal,
  inject,
} from '@angular/core';
import { FormsModule, NgForm } from '@angular/forms';
import { Store } from '@ngrx/store';
import { NgScrollbar } from 'ngx-scrollbar';
import { CommonModule } from '@angular/common';
import { toSignal } from '@angular/core/rxjs-interop';
import { NotificationsService } from '../../../../services/notifications.service';
import { CurrentUserService } from '../../../../services/current-user.service';
import { InteractionBarStateService } from '../../../../services/interaction-bar-state.service';
import { fadeInGeneral } from '../../../../../assets/styles/animations';
import { COMMITMENT_APPROVAL_STATUS } from '../../../../store/commitments/commitments.types';
import { StorageService } from '../../../../services/storage.service';
import { InputCalendarComponent } from '../../../inputs/input-calendar/input-calendar.component';
import { FloatingInputComponent } from '../../../inputs/floating-input/floating-input.component';
import { DropdownComponent } from '../../../inputs/dropdown/dropdown.component';
import { SimpleButtonComponent } from '../../../buttons/simple-medium-button/simple-button.component';
import { SubmittalRegistryItemsComponent } from './submittal-registry-items/submittal-registry-items.component';
import { submittalPackageSidebarActions } from './submittal-package-sidebar.actions';
import { TagDropdownComponent } from '@app/framework/inputs/tag-dropdown/tag-dropdown.component';
import { SPEC_SECTION_REGEX } from '@app/store/submittals/submittals.constants';
import { viewProjectSelectors } from '@app/store/view-project/view-project.selectors';
import { submittalsActions } from '@app/store/submittals/submittals.actions';
import {
  SubmittalPackageCreateUpdateReq,
  SubmittalPackage,
  SubmittalPackageResp,
  SubmittalPackageModel,
  SubmittalPackageModelRegistryItem,
} from '@app/store/submittals/submittals.types';
import { submittalsSelectors } from '@app/store/submittals/submittals.selectors';
import { LoadingSpinnerComponent } from '@app/framework/loading-animation/loading-spinner.component';
import { ProjectDto } from '@app/pages/webapp/projects/projects.interface';
import { SidebarHeaderComponent } from '@app/framework/interaction-bar/sidebar-header/sidebar-header.component';

@Component({
  selector: 'app-submittal-package-sidebar',
  templateUrl: './submittal-package-sidebar.component.html',
  styleUrls: ['./submittal-package-sidebar.component.scss'],
  animations: [fadeInGeneral],
  imports: [
    NgScrollbar,
    FormsModule,
    CommonModule,
    InputCalendarComponent,
    FloatingInputComponent,
    DropdownComponent,
    SimpleButtonComponent,
    TagDropdownComponent,
    SubmittalRegistryItemsComponent,
    LoadingSpinnerComponent,
    SidebarHeaderComponent,
  ],
})
export class SubmittalPackageSidebarComponent {
  protected store = inject(Store);
  protected notif = inject(NotificationsService);
  protected storage = inject(StorageService);
  protected user = inject(CurrentUserService);
  protected interactionBar = inject(InteractionBarStateService);
  protected readonly COMMITMENT_APPROVAL_STATUS = COMMITMENT_APPROVAL_STATUS;
  protected readonly COMMITMENT_SIDEBAR_PAGE = { FIRST_PAGE: 1, LAST_PAGE: 2 };

  readonly SPEC_SECTION_REGEX = SPEC_SECTION_REGEX;

  @ViewChild('submittalForm') submittalForm: NgForm;

  currentPage = signal(1);
  isSubmitting = signal(false); // todo: implement actual submitting state

  projectData = toSignal(this.store.select(viewProjectSelectors.getSelectedProject));
  invitedMembers = toSignal(this.store.select(viewProjectSelectors.selectInvitedMembers));
  invitedMembersIsLoading = toSignal(
    this.store.select(viewProjectSelectors.selectInvitedMembersIsLoading),
  );

  // used when entries get selected on the submittal reg page and a new package should be formed with these entries
  selectedRegistries = input<SubmittalPackageModelRegistryItem[]>();

  // set to non-null value when editing an existing package
  packageId = input<number | null>(null);

  // todo: this should replace temporary teamMembers signal
  // (for now teamMembers signal contains only current user as default participant)
  /*teamMembers = toSignal(this.store.select(viewProjectSelectors.selectTeamMembers));
  teamMembersIsLoading = toSignal(
    this.store.select(viewProjectSelectors.selectTeamMembersIsLoading),
  );*/
  teamMembers = signal<
    {
      id: number;
      name: string;
      company_name: string;
    }[]
  >([
    {
      id: this.user.data.id,
      name: `${this.user.data.first_name} ${this.user.data.last_name}`,
      company_name: this.user.data.company_name,
    },
  ]);

  invitedMemberOptions = computed(() => {
    const invitedMembers = this.invitedMembers();
    const invitedMemberOptions = invitedMembers.map((member) => ({
      id: member.id,
      name: `${member.name}, ${member.company_name}`,
    }));
    return invitedMemberOptions;
  });

  isDropdownShown = {
    participants: false,
  };

  defaultModelValue: SubmittalPackageModel = {
    project_id: null,
    spec_section: '',
    package_version: 1,
    package_title: '',
    submittals_due_date: '',
    package_manager_id: null,
    package_approver_id: null,
    participants: [],
    registry_items: [
      {
        id: null,
        spec_section: null,
        spec_description: null,
        item_description: null,
      },
    ],
  };

  selectedPackage = toSignal(this.store.select(submittalsSelectors.selectPackageDetails));
  model = linkedSignal<
    {
      selectedPackage: SubmittalPackage;
      selectedRegistries: SubmittalPackageModelRegistryItem[];
      projectData: ProjectDto;
    },
    SubmittalPackageModel
  >({
    source: () => ({
      selectedPackage: this.selectedPackage(),
      selectedRegistries: this.selectedRegistries(),
      projectData: this.projectData(),
    }),
    computation: (sources, previousValue) => {
      if (previousValue?.value) {
        return previousValue.value;
      }

      if (!this.packageId()) {
        // create mode
        return {
          ...this.defaultModelValue,
          project_id: sources.projectData?.id,
          registry_items:
            sources.selectedRegistries.length > 0
              ? sources.selectedRegistries
              : this.defaultModelValue.registry_items,
        };
      }

      // edit mode
      return this.convertPackageToModel(sources.selectedPackage);
    },
  });
  isPackageLoading = toSignal(this.store.select(submittalsSelectors.selectPackageDetailsLoading));

  constructor() {
    effect(() => {
      const packageId = this.packageId();
      if (!packageId) {
        this.store.dispatch(submittalPackageSidebarActions.createPackageOpened());
      } else {
        this.store.dispatch(submittalPackageSidebarActions.editPackageOpened({ packageId }));
      }
    });
  }

  updateDueDate(date: string) {
    this.model.update((oldValue) => ({
      ...oldValue,
      submittals_due_date: date,
    }));
  }

  nextPage() {
    if (!this.submittalForm.valid) {
      this.submittalForm.form.markAllAsTouched();
      this.notif.showError('Please fill in all required fields');
      return;
    }

    this.currentPage.set(2);
  }

  previousPage() {
    this.currentPage.set(1);
  }

  save() {
    if (!this.model().project_id) {
      this.notif.showError('Project ID is required');
      return;
    }

    const postData: SubmittalPackageCreateUpdateReq = this.convertModelToPostPackage(this.model());

    if (this.model().id) {
      this.store.dispatch(
        submittalsActions.updateSubmittalPackage({
          submittalPackage: { ...postData, id: this.model().id },
        }),
      );
    } else {
      this.store.dispatch(submittalsActions.createSubmittalPackage({ submittalPackage: postData }));
    }

    this.interactionBar.close();
  }

  goBack() {
    if (this.currentPage() === 2) {
      this.previousPage();
      return;
    }
    this.interactionBar.close();
  }

  private convertModelToPostPackage(model: SubmittalPackageModel): SubmittalPackageCreateUpdateReq {
    if (!model) {
      return null;
    }

    return {
      project_id: model.project_id,
      spec_section: model.spec_section || '',
      title: model.package_title || '',
      due_date: model.submittals_due_date || '',
      package_manager: {
        id: model.package_manager_id,
      },
      package_approver: {
        id: model.package_approver_id,
      },
      participants: model.participants.map((participant) => ({ user_id: participant.id })) || [],
      entries:
        model.registry_items
          ?.filter((item) => item.id !== null)
          .map((item) => ({ id: Number(item.id) })) || [],
    };
  }

  private convertPackageToModel(pkg: SubmittalPackageResp): SubmittalPackageModel {
    if (!pkg) {
      return null;
    }

    return {
      id: pkg.id,
      project_id: pkg.project?.id,
      spec_section: pkg.spec_section,
      package_version: pkg.package_version,
      package_title: pkg.title,
      submittals_due_date: pkg.due_date,
      package_manager_id: pkg.package_manager?.id || null,
      package_approver_id: pkg.package_approver?.id || null,
      participants: pkg.participants.map((participant) => ({
        id: participant.user_id,
        name: participant.name,
      })),
      registry_items: pkg.entries.map((entry) => ({
        id: entry.id,
        spec_section: entry.spec_section,
        spec_description: entry.spec_description,
        item_description: entry.item_description,
      })),
    };
  }

  protected updateModel(key: keyof SubmittalPackageModel, value: any) {
    this.model.update((current) => ({
      ...current,
      [key]: value,
    }));
  }
}
